/**
 * 出貨紀錄 schema(REQ-20260815008)—— 前後端共用。
 *
 * 「訪談建檔 → 新增應收(SalesOrder)→ 商品完成後才實際出貨」的最後一段。
 * 出貨掛在既有的 SalesOrder / SalesOrderLine 底下,**不是另一張獨立單據**
 * (REQ-20260519001 SCOPE §3 Phase 2 原本那樣規劃,但實作路線已改,見 REQUEST.md)。
 *
 * 已出貨數量**不是資料庫欄位**,一律由 `shipment_lines` 即時 groupBy 算出來,
 * 慣例沿用「已收款金額由 ar_settlement_lines 算」(REQ-20260806002 §2.1)。
 */
import { z } from 'zod';
import { nonNegativeDecimalString, positiveDecimalString } from './decimal.schema';

/**
 * 一張訂單的出貨進度。訂單上沒有這個欄位,是拿「訂單品項數量合計」與
 * 「已出貨數量合計」比出來的:
 *   FULL    已出貨(出滿了,或訂單本身沒有數量可出)
 *   PARTIAL 部分出貨
 *   NONE    待出貨
 */
export const ORDER_SHIPMENT_STATUSES = ['NONE', 'PARTIAL', 'FULL'] as const;
export type OrderShipmentStatus = (typeof ORDER_SHIPMENT_STATUSES)[number];

/** 顯示用中文。前後端都用這一份,不要各自寫一組對照。 */
export const ORDER_SHIPMENT_STATUS_LABELS: Record<OrderShipmentStatus, string> = {
  NONE: '待出貨',
  PARTIAL: '部分出貨',
  FULL: '已出貨',
};

/**
 * 本次出貨的一個品項。`orderLineId` 是 `sales_order_lines.id`(真的 FK,
 * 與沖帳明細那個刻意不設 FK 的字串單號不同,見 migration 0035 的說明)。
 * 數量由後端拿「該品項數量 − 目前已出貨數量」驗證,超出一律 400 而不是靜默夾成上限。
 */
export const CreateShipmentLineSchema = z.object({
  orderLineId: z.number().int().positive(),
  qty: positiveDecimalString,
});
export type CreateShipmentLineInput = z.infer<typeof CreateShipmentLineSchema>;

/**
 * 送貨時順便收到的錢(REQ-20260815010)。欄位名稱與型別**逐字比照**
 * `CreateArSettlementSchema` —— 這幾個值最後就是原封不動寫進 `ar_settlements`
 * 的同名欄位,換個名字只會在兩邊之間多一層要對照的翻譯。
 *
 * **全部留白時,行為與加這組欄位之前完全相同**(只登記出貨,不建立任何沖帳紀錄)。
 */
export interface ShipmentReceiptFields {
  cashAmount?: string | null | undefined;
  checkAmount?: string | null | undefined;
  /** 支票兌現日期 YYYY-MM-DD(REQ-20260817006),與 `checkAmount` 不互相要求 */
  checkClearDate?: string | null | undefined;
  /** 匯款金額(REQ-20260817006)。規則與現金 / 支票相同 */
  transferAmount?: string | null | undefined;
  /** 匯款帳號末 4 碼(REQ-20260817006) */
  transferAccountLast4?: string | null | undefined;
  usePrepaid?: string | null | undefined;
  addPrepaid?: string | null | undefined;
}

/**
 * 本次要沖掉這張單多少 = 現金 + 支票 + 匯款 + 動用預收款。
 *
 * **`addPrepaid` 不算**:它是「多收的錢轉存成預收款」,沒有沖掉任何應收
 * (語意同 `createManual()` 裡 `totalSettled` 不含 `addPrepaid`)。
 *
 * 登錄收款彈窗是「勾單據 → 每張填要沖多少」,收款方式金額只是註記;出貨彈窗
 * **只有這一張單**,再叫使用者把同一個數字打兩次沒有意義,所以這裡反過來 ——
 * 收到的錢就是沖掉這張單的錢。
 *
 * 回傳 number 只用於**畫面即時回饋與擋下明顯的錯**;真正寫進 DB 的金額由後端
 * 以 `Prisma.Decimal` 重算(慣例同 `calcOrderTotals()` 與 `accounting-amounts.ts`)。
 */
export function shipmentReceiptTotal(v: ShipmentReceiptFields): number {
  const cents = [v.cashAmount, v.checkAmount, v.transferAmount, v.usePrepaid].reduce(
    (sum, a) => sum + Math.round((Number(a) || 0) * 100),
    0,
  );
  return cents / 100;
}

/**
 * POST /sales-orders/:id/shipments body。
 *
 * 只送「這次出了什麼」——可以只出部分品項、也可以分多次出完,
 * 每次都是一筆獨立的 Shipment(與收款登錄同一個互動模型)。
 *
 * 「送貨順便收錢」(REQ-20260815010)是**選填**的第二件事:帶了收款金額才會
 * 一併建立沖帳紀錄,而且固定沖這張單(不能挑別張 —— 挑單是登錄收款彈窗的事)。
 * 收款日期直接沿用 `shippedAt`:這個情境下錢與貨是同一趟,分成兩個日期欄位
 * 只會多一個能填錯、且沒有人會去填不同值的欄位。真的要補登不同日期的收款,
 * 走既有的登錄收款畫面。
 */
export const CreateShipmentSchema = z
  .object({
    /** 出貨日期 YYYY-MM-DD(台北日期,容器是 UTC 不能用 new Date() 直接切) */
    shippedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '出貨日期格式應為 YYYY-MM-DD'),
    note: z.string().trim().max(200).optional().nullable(),
    lines: z.array(CreateShipmentLineSchema).min(1, '至少要有一個出貨品項'),
    /** 以下為選填的「本次收款」,全部留白 = 只登記出貨 */
    cashAmount: nonNegativeDecimalString.optional().nullable(),
    checkAmount: nonNegativeDecimalString.optional().nullable(),
    /**
     * 支票兌現日期 YYYY-MM-DD(REQ-20260817006)。與 `checkAmount` **不互相要求** ——
     * 兌現日通常是收票當下就寫在票面上,但也有先收票、日期後補的情況,
     * 硬綁等式只會把正常的單擋掉(理由同 `CreateArSettlementSchema` 不強制
     * 「現金 + 支票 = 沖帳合計」)。
     */
    checkClearDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, '支票兌現日期格式應為 YYYY-MM-DD')
      .optional()
      .nullable(),
    /** 匯款(REQ-20260817006)。與現金 / 支票同層級,都算「實收的錢」 */
    transferAmount: nonNegativeDecimalString.optional().nullable(),
    /** 匯款帳號末 4 碼(REQ-20260817006)。只有填了匯款金額才有意義,但不互相驗證 */
    transferAccountLast4: z
      .string()
      .regex(/^\d{4}$/, '匯款帳號末 4 碼應為 4 位數字')
      .optional()
      .nullable(),
    /** 動用客戶預收款 */
    usePrepaid: nonNegativeDecimalString.optional().nullable(),
    /** 本次多收、轉入預收款 */
    addPrepaid: nonNegativeDecimalString.optional().nullable(),
    /**
     * 寄送方式(REQ-20260908002 圖一)。**新建立的出貨必填** —— 四選一,
     * 只有 `CARRIER`(委外貨運)會展開下面那四格。
     *
     * ⚠️ DB 欄位是 nullable 的:功能上線前的既有出貨紀錄當時沒有這個維度,
     * **不回填猜測值**(見 migration 0048 §2)。必填只在這裡與前端生效。
     */
    shippingMethod: z.enum(['PICKUP', 'SELF_DELIVERY', 'CARRIER', 'OTHER'], {
      required_error: '請選擇寄送方式',
      invalid_type_error: '請選擇寄送方式',
    }),
    /** 寄送方式選「其他」時的說明,其餘三種一律 null */
    shippingMethodNote: z.string().trim().max(100).optional().nullable(),
    /**
     * 以下為「委外貨運」才展開的欄位(REQ-20260817007 起就存在的三格 + 付款方式)。
     *
     * 承運商是 `suppliers.id` —— 貨運公司一律要先建檔成廠商(需求原文的要求),
     * 不收自由文字。託運單號仍然**不與其他欄位互相要求**:先寄出、單號晚一點才
     * 拿到是常態,硬綁等式只會把正常的單擋掉。
     */
    carrierSupplierId: z.number().int().positive().optional().nullable(),
    trackingNo: z.string().trim().max(50).optional().nullable(),
    /**
     * 運費金額。⚠️ **未稅**(REQ-20260908002 圖一明確定義)——
     * REQ-20260829003 當時把它當含稅總額,那正是「回推會有尾差」進而選免稅的前提;
     * 前提沒了,免稅那個結論也跟著推翻(見 REQUEST.md §Step 5)。
     */
    shippingFee: nonNegativeDecimalString.optional().nullable(),
    /**
     * 這筆運費要不要變成應付(REQ-20260908002)。取代 REQ-20260829003 的
     * 「☑ 運費已付款 + 現金 / 支票」—— 那組問的是「用什麼工具付」,
     * 而歸單流程要先知道的是「會不會進應付」。
     * 值域與 `purchase_order_lines.linePaymentType` 逐字相同(歸單時原封不動寫過去)。
     */
    freightPaymentType: z.enum(['MONTHLY', 'CASH', 'COLLECT']).optional().nullable(),
  })
  .superRefine((v, ctx) => {
    // 只填「新增預收款」時沒有任何沖銷金額,那筆收款會無聲無息地不見 ——
    // 與「這次出貨數量超賣」是兩件不同的事,訊息也掛在收款欄位上
    if (Number(v.addPrepaid ?? 0) > 0 && shipmentReceiptTotal(v) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cashAmount'],
        message: '要登記預收款,請一併填寫本次實際收到的現金 / 支票 / 匯款金額',
      });
    }
    // 以下三條都只在「委外貨運」時成立 —— 其餘三種寄送方式一格都不展開,
    // 要求它們填等於把自取的單擋在門外。
    if (v.shippingMethod === 'CARRIER') {
      if (v.carrierSupplierId == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['carrierSupplierId'],
          message: '委外貨運請選擇貨運公司',
        });
      }
      if (v.freightPaymentType == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['freightPaymentType'],
          message: '委外貨運請選擇付款方式',
        });
      }
      // 到付是收貨方付錢給貨運行,金額不是我方的成本 —— 留白也能送出,
      // 日後要查再到歸單畫面補(PATCH /freight-lines/:id)。
      if (v.freightPaymentType !== 'COLLECT' && !(Number(v.shippingFee) > 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['shippingFee'],
          message: '委外貨運請填寫運費金額(到付可留白)',
        });
      }
    }
    if (v.shippingMethod === 'OTHER' && !v.shippingMethodNote?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shippingMethodNote'],
        message: '寄送方式選「其他」時請說明',
      });
    }
  });
export type CreateShipmentInput = z.infer<typeof CreateShipmentSchema>;

/**
 * PATCH /sales-orders/:id/shipments/:shipmentId body(REQ-20260825003)。
 *
 * **只有出貨日期一個欄位** —— 使用者 KEY 錯日期時,唯一要救的就是那一格;
 * 數量 / 品項改了會讓已出貨數量對不回出貨紀錄(那是「作廢重開」的範圍)。
 * 驗證規則與 `CreateShipmentSchema.shippedAt` 逐字相同:同一個欄位不該因為
 * 走的是新增還是修改而有兩套格式要求。
 *
 * 後端另外擋「這張單已經有沖帳紀錄」—— 錢已經入帳的單連日期都不給動。
 */
export const UpdateShipmentDateSchema = z.object({
  /** 出貨日期 YYYY-MM-DD(台北日期,容器是 UTC 不能用 new Date() 直接切) */
  shippedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '出貨日期格式應為 YYYY-MM-DD'),
});
export type UpdateShipmentDateInput = z.infer<typeof UpdateShipmentDateSchema>;

/** 一筆出貨紀錄(單頭 + 這次出了哪些品項多少)。 */
export interface ShipmentListItem {
  id: number;
  uuid: string;
  /** ISO 字串 */
  shippedAt: string;
  note: string | null;
  /** 登記人姓名;帳號被刪或歷史補登時為 null,畫面顯示「—」 */
  shippedByName: string | null;
  /**
   * 寄送方式(REQ-20260908002)。功能上線前的舊紀錄一律 null(不回填)——
   * 畫面上顯示「—」而不是猜一個。
   */
  shippingMethod: string | null;
  shippingMethodNote: string | null;
  /** 寄送資訊(REQ-20260817007)。自取或還沒記的那幾筆一律是 null */
  carrierSupplierId: number | null;
  /** 承運商簡稱(顯示用,從 Supplier join 出來) */
  carrierName: string | null;
  trackingNo: string | null;
  shippingFee: string | null;
  /**
   * 這筆寄送自動產生的運費進貨單(REQ-20260829003)。
   *
   * ⚠️ **REQ-20260908002 起不再有新的值** —— 出貨端改成只寫一筆運費明細
   * (`freight_lines`),進貨單要等到月底歸單才建。**欄位與既有資料保留不動**:
   * 舊流程已經開出來的那幾張單是歷史事實,仍然要查得到。
   */
  freightPurchaseOrderId: number | null;
  /** 運費進貨單號(顯示用,從 PurchaseOrder join 出來,不在出貨紀錄上 freeze) */
  freightPurchaseOrderNo: string | null;
  /**
   * 這次出貨產生的運費明細(REQ-20260908002)。只有「委外貨運」才有;
   * `status` 讓畫面判斷出貨異動時該顯示哪一種提示(見 REQUEST.md §Step 2)。
   */
  freightLine: {
    id: number;
    status: string;
    amount: string | null;
    paymentType: string;
    workOrderState: string;
  } | null;
  createdAt: string;
  lines: Array<{
    orderLineId: number;
    lineNo: number;
    productName: string;
    qty: string;
  }>;
}

/** 訂單品項的出貨進度。`remainingQty` = orderedQty − shippedQty,不會是負數。 */
export interface OrderShipmentLine {
  orderLineId: number;
  lineNo: number;
  productName: string;
  productSpec: string | null;
  unitName: string | null;
  orderedQty: string;
  shippedQty: string;
  remainingQty: string;
}

/** GET /sales-orders/:id/shipments —— 出貨彈窗要的全部資料 */
export interface OrderShipmentsResponse {
  orderId: number;
  orderNo: string;
  /** 單據狀態(DRAFT / POSTED / VOIDED / HISTORICAL) */
  orderStatus: string;
  shipmentStatus: OrderShipmentStatus;
  /** 現在能不能登記出貨 = 已過帳。前端據此決定要不要顯示輸入框 */
  canShip: boolean;
  /**
   * 這張單還沒收到的錢(REQ-20260815010)—— 總額 − 已沖 − 折讓,算法同未結應收明細。
   * 送貨要順便收錢的人得知道該收多少,前端也拿它擋超收。可能是負數(溢收)。
   */
  outstanding: string;
  lines: OrderShipmentLine[];
  shipments: ShipmentListItem[];
}

/** 未出貨彙總的一列 —— 儀表板清單用,只放看得懂「還欠誰什麼」需要的欄位 */
export interface UnshippedOrderItem {
  orderId: number;
  orderNo: string;
  txnDate: string;
  customerId: number;
  customerSimpleName: string;
  /**
   * 這張單還沒出完的品項名稱,依訂單品項原始順序、重複的只留一個
   * (同一個品名分成兩列的單不必在儀表板上把名字印兩次)。
   */
  productNames: string[];
  /** 這張單的未出貨數量合計 */
  unshippedQty: string;
  shipmentStatus: OrderShipmentStatus;
}

/**
 * GET /shipments/unshipped-summary。
 *
 * 只算 **POSTED** 的單:草稿還沒定案、作廢單不該再出、歷史匯入單(HISTORICAL)
 * 是舊系統的帳,不是這套系統要追的出貨。
 *
 * `items` 是**完整**清單(最舊的優先);原本有 50 筆上限,新單排在最後會被擋在
 * 畫面外,REQ-20260829001 起改由前端決定預設顯示幾筆。
 * `orderCount` / `lineCount` / `unshippedQty` 一如既往是完整的統計。
 */
export interface UnshippedSummaryResponse {
  orderCount: number;
  lineCount: number;
  unshippedQty: string;
  items: UnshippedOrderItem[];
}
